import { FormInstance } from 'antd';
import { useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useCreateDataExtractionByModel, useCreateDataExtractionQueue } from '@/hooks';

interface UseConfirmationModalModelParams {
	modelId: string;
	form: FormInstance;
	onCancel: () => void;
}

export const useConfirmationModalModel = ({ form, modelId, onCancel }: UseConfirmationModalModelParams) => {
	const history = useHistory();
	const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
	const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);

	const { mutateAsync: createDataExtractionQueue } = useCreateDataExtractionQueue();
	const { mutateAsync: createDataExtractionByModel } = useCreateDataExtractionByModel();

	const handleConfirm = useCallback(async () => {
		const values = await form.validateFields();

		if (!values) return;

		const templateResponse = await createDataExtractionByModel({
			organization_id: values.organization_id!,
			company_id: values.company_id!,
			data_extraction_model_id: modelId!,
			extraction_name: values.name!,
			sector_id: values.sector_id!,
			workstation_id: values.workstation_id!,
			line_id: values.line_id!,
			start_date: values.start_date!,
			end_date: values.end_date!
		});

		const templateId = templateResponse.id;

		const queuePayload = {
			organization_id: values.organization_id!,
			company_id: values.company_id!,
			data_extraction_id: templateId
		};

		await createDataExtractionQueue(queuePayload);
		setShowSuccessModal(true);
	}, [form, modelId, createDataExtractionByModel, createDataExtractionQueue]);

	const handleSuccessModalClose = useCallback(() => {
		setShowSuccessModal(false);
		onCancel();
	}, [onCancel]);

	const handleErrorModalClose = useCallback(() => {
		setShowErrorModal(false);
	}, []);

	const handleGoToHome = useCallback(() => {
		setShowSuccessModal(false);
		onCancel();
		history.push('/settings/data-extraction');
	}, [onCancel, history]);

	return {
		showSuccessModal,
		showErrorModal,
		handleConfirm,
		handleSuccessModalClose,
		handleErrorModalClose,
		handleGoToHome
	};
};
